/**
 * Field Priority Classification for Expert Labeling
 *
 * Defines priority levels (M, GTF, O) for profile fields to help experts
 * understand which fields are most critical for equipment recommendations.
 *
 * Based on: FIELD_PRIORITY_CLASSIFICATION.md
 */

export type PriorityLevel = 'M' | 'GTF' | 'O';

export interface FieldPriority {
  priority: PriorityLevel;
  label: string;
  category: string;
}

export const FIELD_PRIORITIES: Record<string, FieldPriority> = {
  // ==================== MANDATORY (M) - 18 fields ====================

  // Basic Information (M)
  handicap: { priority: 'M', label: 'Handicap', category: 'Basic Info' },
  age: { priority: 'M', label: 'Age', category: 'Basic Info' },
  height_inches: { priority: 'M', label: 'Height (inches)', category: 'Basic Info' },
  years_playing: { priority: 'M', label: 'Years Playing', category: 'Basic Info' },
  dominant_hand: { priority: 'M', label: 'Dominant Hand', category: 'Basic Info' },
  budget_range: { priority: 'M', label: 'Budget Range (new clubs only, latest models)', category: 'Basic Info' },

  // Swing Characteristics (M)
  swing_speed_driver: { priority: 'M', label: 'Driver Swing Speed (mph)', category: 'Swing' },
  attack_angle_driver: { priority: 'M', label: 'Driver Attack Angle (°)', category: 'Swing' },

  // Ball Flight - Driver (M)
  launch_angle_driver: { priority: 'M', label: 'Launch Angle (°)', category: 'Ball Flight - Driver' },
  spin_rate_driver: { priority: 'M', label: 'Spin Rate (rpm)', category: 'Ball Flight - Driver' },
  ball_speed_driver: { priority: 'M', label: 'Ball Speed (mph)', category: 'Ball Flight - Driver' },

  // Ball Flight - 7-Iron (M)
  launch_angle_7i: { priority: 'M', label: 'Launch Angle (°)', category: 'Ball Flight - 7-Iron' },
  spin_rate_7i: { priority: 'M', label: 'Spin Rate (rpm)', category: 'Ball Flight - 7-Iron' },
  ball_speed_7i: { priority: 'M', label: 'Ball Speed (mph)', category: 'Ball Flight - 7-Iron' },

  // Current Equipment - Shaft Flex (M)
  current_driver_shaft_flex: { priority: 'M', label: 'Current Driver Shaft Flex', category: 'Current Equipment' },
  current_iron_shaft_flex: { priority: 'M', label: 'Current Iron Shaft Flex', category: 'Current Equipment' },

  // Goals & Challenges (M)
  primary_goal_structured: { priority: 'M', label: 'Primary Goal', category: 'Goals & Challenges' },
  biggest_frustration: { priority: 'M', label: 'Biggest Frustration', category: 'Goals & Challenges' },

  // ==================== GOOD TO FILL (GTF) - 30+ fields ====================

  // Swing Characteristics (GTF)
  swing_speed_7iron: { priority: 'GTF', label: '7-Iron Swing Speed (mph)', category: 'Swing' },
  attack_angle_irons: { priority: 'GTF', label: 'Iron Attack Angle (°)', category: 'Swing' },

  // Ball Flight - Driver (GTF)
  driver_carry_distance: { priority: 'GTF', label: 'Carry Distance (yds)', category: 'Ball Flight - Driver' },
  face_to_path_driver: { priority: 'GTF', label: 'Face to Path (°)', category: 'Ball Flight - Driver' },

  // Ball Flight - 7-Iron (GTF)
  iron_carry_distance: { priority: 'GTF', label: 'Carry Distance (yds)', category: 'Ball Flight - 7-Iron' },
  face_to_path_7i: { priority: 'GTF', label: 'Face to Path (°)', category: 'Ball Flight - 7-Iron' },

  // Physical Profile (GTF)
  flexibility_rating: { priority: 'GTF', label: 'Flexibility (1-10)', category: 'Physical Profile' },
  core_strength: { priority: 'GTF', label: 'Core Strength (1-10)', category: 'Physical Profile' },
  weekly_workouts: { priority: 'GTF', label: 'Weekly Workouts', category: 'Physical Profile' },

  // Current Equipment - Satisfaction (GTF)
  current_driver_satisfaction: { priority: 'GTF', label: 'Driver Satisfaction (1-10)', category: 'Current Equipment' },
  current_iron_satisfaction: { priority: 'GTF', label: 'Iron Satisfaction (1-10)', category: 'Current Equipment' },
  current_wedge_satisfaction: { priority: 'GTF', label: 'Wedge Satisfaction (1-10)', category: 'Current Equipment' },

  // Current Equipment - Details (GTF)
  current_driver_brand: { priority: 'GTF', label: 'Current Driver Brand', category: 'Current Equipment' },
  current_driver_model: { priority: 'GTF', label: 'Current Driver Model', category: 'Current Equipment' },
  current_driver_shaft_model: { priority: 'GTF', label: 'Driver Shaft Model', category: 'Current Equipment' },
  current_driver_shaft_weight: { priority: 'GTF', label: 'Driver Shaft Weight (g)', category: 'Current Equipment' },

  current_iron_brand: { priority: 'GTF', label: 'Current Iron Brand', category: 'Current Equipment' },
  current_iron_model: { priority: 'GTF', label: 'Current Iron Model', category: 'Current Equipment' },
  current_iron_shaft_model: { priority: 'GTF', label: 'Iron Shaft Model', category: 'Current Equipment' },
  current_iron_shaft_weight: { priority: 'GTF', label: 'Iron Shaft Weight (g)', category: 'Current Equipment' },

  current_wedge_brand: { priority: 'GTF', label: 'Current Wedge Brand', category: 'Current Equipment' },
  current_wedge_model: { priority: 'GTF', label: 'Current Wedge Model', category: 'Current Equipment' },
  current_wedge_lofts: { priority: 'GTF', label: 'Wedge Lofts', category: 'Current Equipment' },

  // Favorite Club (GTF)
  favorite_club_brand: { priority: 'GTF', label: 'Favorite Club Brand', category: 'All-Time Favorite Club' },
  favorite_club_model: { priority: 'GTF', label: 'Favorite Club Model', category: 'All-Time Favorite Club' },
  favorite_club_type: { priority: 'GTF', label: 'Favorite Club Type', category: 'All-Time Favorite Club' },
  favorite_club_shaft_flex: { priority: 'GTF', label: 'Favorite Shaft Flex', category: 'All-Time Favorite Club' },
  favorite_club_shaft_model: { priority: 'GTF', label: 'Favorite Shaft Model', category: 'All-Time Favorite Club' },
  favorite_club_shaft_weight: { priority: 'GTF', label: 'Favorite Shaft Weight (g)', category: 'All-Time Favorite Club' },

  // Equipment History (GTF)
  equipment_change_frequency: { priority: 'GTF', label: 'Equipment Change Frequency', category: 'Equipment History' },
  budget_flexibility: { priority: 'GTF', label: 'Budget Flexibility', category: 'Equipment History' },
  fitted_by_professional: { priority: 'GTF', label: 'Custom Fitting History', category: 'Equipment History' },

  // Wedge Preferences (GTF)
  preferred_wedge_for_chipping: { priority: 'GTF', label: 'Preferred Wedge for Chipping', category: 'Wedge Preferences' },
  preferred_wedge_for_bunkers: { priority: 'GTF', label: 'Preferred Wedge for Bunkers', category: 'Wedge Preferences' },
  lies_wedge: { priority: 'GTF', label: 'Typical Lies', category: 'Wedge Preferences' },
  preferred_trajectory_wedge: { priority: 'GTF', label: 'Preferred Trajectory', category: 'Wedge Preferences' },

  // Preferences (GTF)
  materials_interest_irons: { priority: 'GTF', label: 'Iron Material Preference', category: 'Preferences' },
  materials_interest_drivers: { priority: 'GTF', label: 'Driver Material Preference', category: 'Preferences' },
  shiftable_lofts_drivers: { priority: 'GTF', label: 'Adjustable Loft Interest', category: 'Preferences' },
  bendable_irons: { priority: 'GTF', label: 'Bendable Irons Interest', category: 'Preferences' },

  // Goals & Challenges (GTF)
  target_handicap: { priority: 'GTF', label: 'Target Handicap', category: 'Goals & Challenges' },
  purchase_timeline: { priority: 'GTF', label: 'Purchase Timeline', category: 'Goals & Challenges' },
  practice_frequency: { priority: 'GTF', label: 'Practice Frequency', category: 'Goals & Challenges' },
  lesson_frequency: { priority: 'GTF', label: 'Lesson Frequency', category: 'Goals & Challenges' },

  // Playing Profile (GTF)
  rounds_per_year: { priority: 'GTF', label: 'Rounds per Year', category: 'Playing Profile' },
  typical_course_type: { priority: 'GTF', label: 'Typical Course Type', category: 'Playing Profile' },
  scoring_consistency: { priority: 'GTF', label: 'Scoring Consistency', category: 'Playing Profile' },

  // ==================== OPTIONAL (O) - 27+ fields ====================

  // Physical Profile (O)
  wrist_to_floor: { priority: 'O', label: 'Wrist to Floor (inches)', category: 'Physical Profile' },
  posture_type: { priority: 'O', label: 'Posture Type', category: 'Physical Profile' },

  // Ball Flight - Wedge (O)
  launch_angle_wedge: { priority: 'O', label: 'Launch Angle (°)', category: 'Ball Flight - Wedge' },
  spin_rate_wedge: { priority: 'O', label: 'Spin Rate (rpm)', category: 'Ball Flight - Wedge' },
  ball_speed_wedge: { priority: 'O', label: 'Ball Speed (mph)', category: 'Ball Flight - Wedge' },
  wedge_carry_distance: { priority: 'O', label: 'Carry Distance (yds)', category: 'Ball Flight - Wedge' },

  // Preferences (O)
  feedback_preference: { priority: 'O', label: 'Feel Preference', category: 'Preferences' },
  sound_preference: { priority: 'O', label: 'Sound Preference', category: 'Preferences' },
  visual_alignment_aids: { priority: 'O', label: 'Visual Alignment Aids', category: 'Preferences' },
  offset_preference: { priority: 'O', label: 'Offset Preference', category: 'Preferences' },
  finish_style_preference: { priority: 'O', label: 'Finish Style', category: 'Preferences' },
  grip_type_preference: { priority: 'O', label: 'Grip Type', category: 'Preferences' },
  clubs_interest_general: { priority: 'O', label: 'General Club Interests', category: 'Preferences' },
  clubs_interest_looks: { priority: 'O', label: 'Aesthetic Interests', category: 'Preferences' },

  // Playing Profile (O)
  course_difficulty: { priority: 'O', label: 'Course Difficulty', category: 'Playing Profile' },
  typical_weather: { priority: 'O', label: 'Typical Weather', category: 'Playing Profile' },
  performance_under_pressure: { priority: 'O', label: 'Performance Under Pressure', category: 'Playing Profile' },
  driver_under_pressure: { priority: 'O', label: 'Driver Confidence (1-10)', category: 'Playing Profile' },
  irons_under_pressure: { priority: 'O', label: 'Iron Confidence (1-10)', category: 'Playing Profile' },
  wedges_under_pressure: { priority: 'O', label: 'Wedge Confidence (1-10)', category: 'Playing Profile' },
  playing_style: { priority: 'O', label: 'Playing Style', category: 'Playing Profile' },

  // Goals & Challenges (O)
  technology_comfort: { priority: 'O', label: 'Technology Comfort', category: 'Goals & Challenges' },
  in_season_off_season: { priority: 'O', label: 'Seasonal Play', category: 'Goals & Challenges' },

  // Other fields
  current_driver_loft: { priority: 'O', label: 'Driver Loft (°)', category: 'Current Equipment' },
  pitching_wedge_distance: { priority: 'O', label: 'Pitching Wedge Distance (yds)', category: 'Performance' },
  distance_priority: { priority: 'O', label: 'Distance Priority (1-10)', category: 'Performance' },
  accuracy_priority: { priority: 'O', label: 'Accuracy Priority (1-10)', category: 'Performance' },
  consistency_priority: { priority: 'O', label: 'Consistency Priority (1-10)', category: 'Performance' },
};

/**
 * Get field priority information
 */
export function getFieldPriority(fieldName: string): FieldPriority | null {
  return FIELD_PRIORITIES[fieldName] || null;
}

/**
 * Group profile fields by category with priority information
 */
export function groupFieldsByCategory(profile: any): Record<string, any[]> {
  const grouped: Record<string, any[]> = {};

  Object.entries(profile).forEach(([key, value]) => {
    // Skip null/undefined values and non-user fields
    if (value === null || value === undefined) return;
    if (['id', 'user_id', 'is_synthetic', 'created_at', 'updated_at', 'date_of_birth', 'country', 'state', 'user_email'].includes(key)) return;

    const priority = getFieldPriority(key);
    if (!priority) return; // Skip unmapped fields

    if (!grouped[priority.category]) {
      grouped[priority.category] = [];
    }

    grouped[priority.category].push({
      key,
      value,
      label: priority.label,
      priority: priority.priority
    });
  });

  return grouped;
}

/**
 * Get field count by priority level
 */
export function getFieldCountByPriority(profile: any): { M: number; GTF: number; O: number } {
  const counts = { M: 0, GTF: 0, O: 0 };

  Object.entries(profile).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    const priority = getFieldPriority(key);
    if (priority) {
      counts[priority.priority]++;
    }
  });

  return counts;
}

/**
 * Sort categories by importance (put M fields first, then GTF, then O)
 */
export function sortCategoriesByPriority(categories: string[], groupedFields: Record<string, any[]>): string[] {
  return categories.sort((a, b) => {
    // Get highest priority in each category
    const getPriorityScore = (cat: string) => {
      const fields = groupedFields[cat];
      if (!fields || fields.length === 0) return 3;

      const priorities = fields.map(f => f.priority);
      if (priorities.includes('M')) return 0;
      if (priorities.includes('GTF')) return 1;
      return 2;
    };

    return getPriorityScore(a) - getPriorityScore(b);
  });
}
